import React from "react";

import AssignmentIndOutlinedIcon from "@mui/icons-material/AssignmentIndOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import HowToVoteOutlinedIcon from "@mui/icons-material/HowToVoteOutlined";
import SupervisorAccountOutlinedIcon from "@mui/icons-material/SupervisorAccountOutlined";
import GpsCoordinates from "@auto/widgets/GpsCoordinates";

import { displayEdit, buildEmptyObject } from "@auto/widgets/EditComponent";

import { rest, t } from "@auto/services";
import { getServiceUri } from "@auto/metadata";
import { can } from "@src/security/permissions";

import styles from "./VoterApplicationForm.module.styl";

const fieldGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
  gap: "16px",
  width: "100%",
};

const applicantLayoutStyle = {
  display: "grid",
  gridTemplateColumns: "180px minmax(0, 1fr)",
  gap: "24px",
  alignItems: "start",
  width: "100%",
};

const portraitBoxStyle = {
  width: "180px",
  height: "220px",
  borderRadius: "16px",
  overflow: "hidden",
  border: "1px solid rgba(15, 23, 42, 0.12)",
  background: "#f8fafc",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#64748b",
  fontSize: "13px",
  fontWeight: 600,
};

const portraitImageStyle = {
  width: "100%",
  height: "100%",
  objectFit: "cover",
  display: "block",
};

const fieldStyle = {
  minWidth: 0,
  border: "1px solid rgba(15, 23, 42, 0.10)",
  borderRadius: "14px",
  padding: "14px 16px",
  background: "#f8fafc",
};

const fieldLabelStyle = {
  fontSize: "12px",
  lineHeight: "16px",
  fontWeight: 700,
  letterSpacing: "0.04em",
  color: "#64748b",
  textTransform: "uppercase",
  marginBottom: "8px",
};

const fieldValueStyle = {
  fontSize: "16px",
  lineHeight: "22px",
  fontWeight: 700,
  color: "#0f172a",
  wordBreak: "normal",
  overflowWrap: "break-word",
  whiteSpace: "normal",
};

const linkValueStyle = {
  ...fieldValueStyle,
  border: 0,
  padding: 0,
  margin: 0,
  background: "transparent",
  color: "#2563eb",
  cursor: "pointer",
  textAlign: "left",
};

const formatDateTime = (value) => {
  if (!value) {
    return "";
  }

  if (typeof value === "string") {
    return value.replace("T", " ").slice(0, 19);
  }

  try {
    return new Date(value).toISOString().replace("T", " ").slice(0, 19);
  } catch {
    return String(value);
  }
};

const getField = (row, camelName, snakeName) => {
  return row?.[camelName] ?? row?.[snakeName] ?? "";
};

const normalizeApplicationType = (value) => {
  const raw =
    value === null || value === undefined
      ? ""
      : String(value).trim().toUpperCase();

  switch (raw) {
    case "1":
    case "STANDARD":
      return "STANDARD";

    case "2":
    case "CORRECTION":
      return "CORRECTION";

    default:
      return raw;
  }
};

const applicationTypeLabel = (value) => {
  switch (normalizeApplicationType(value)) {
    case "CORRECTION":
      return t("voter.correction");

    case "STANDARD":
    default:
      return t("voter.standard");
  }
};

const isCorrectionApplication = (application) => {
  return normalizeApplicationType(
    getField(application, "applicationType", "application_type")
  ) === "CORRECTION";
};

const normalizeStatus = (value) => {
  const raw =
    value === null || value === undefined
      ? ""
      : String(value).trim().toUpperCase();

  switch (raw) {
    case "1":
    case "SUBMITTED":
      return "SUBMITTED";

    case "3":
    case "APPROVED":
      return "APPROVED";

    case "4":
    case "MANUAL_ADJUDICATION":
    case "MANUAL ADJUDICATION":
      return "MANUAL_ADJUDICATION";

    case "5":
    case "RETRANSMISSION":
      return "RETRANSMISSION";

    case "6":
    case "DUPLICATE":
      return "DUPLICATE";

    case "7":
    case "REJECTED":
      return "REJECTED";

    case "8":
    case "ARCHIVED":
      return "ARCHIVED";

    case "9":
    case "CHECKED":
      return "CHECKED";

    case "10":
    case "PERSON_RESOLVED":
    case "PERSON RESOLVED":
      return "PERSON_RESOLVED";

    case "11":
    case "DEDUPLICATED":
      return "DEDUPLICATED";

    case "12":
    case "FACE_VERIFICATION_MISMATCH":
    case "PERSON FACE_VERIFICATION_MISMATCH":
      return "FACE_VERIFICATION_MISMATCH";

    default:
      return raw;
  }
};

const statusLabel = (status) => {
  switch (normalizeStatus(status)) {
    case "SUBMITTED":
      return t("common.submitted");

    case "APPROVED":
      return t("common.approved");

    case "MANUAL_ADJUDICATION":
      return t("voter.manualAdjudication");

    case "RETRANSMISSION":
      return t("voter.retransmission");

    case "DUPLICATE":
      return t("voter.duplicate");

    case "REJECTED":
      return t("common.rejected");

    case "ARCHIVED":
      return t("common.archived");

    case "CHECKED":
      return t("voter.checked");

    case "PERSON_RESOLVED":
      return t("voter.personResolved");

    case "DEDUPLICATED":
      return t("voter_application.deduplicated_status");

    case "FACE_VERIFICATION_MISMATCH":
      return t("voter_application.face_verification_mismatch_status");

    default:
      return normalizeStatus(status) || t("common.submitted");
  }
};

const getStatusMeta = (status) => {
  switch (normalizeStatus(status)) {
    case "APPROVED":
      return {
        label: statusLabel(status),
        className: styles.statusApproved,
      };

    case "MANUAL_ADJUDICATION":
      return {
        label: statusLabel(status),
        className: styles.statusRejected,
      };

    case "SUBMITTED":
    default:
      return {
        label: statusLabel(status),
        className: styles.statusPending,
      };
  }
};

const formatSource = (value) => {
  const raw =
    value === null || value === undefined
      ? ""
      : String(value).trim();

  switch (raw) {
    case "1":
      return t("voter.oniSearch");

    case "2":
      return t("voter.mrzNfc");

    case "3":
      return t("voter.manualEntry");

    case "4":
      return t("voter.selfService");

    default:
      return raw;
  }
};

const readByGid = async (entity, gid) => {
  if (!gid) {
    return null;
  }

  return await rest.request(getServiceUri() + `${entity}/${gid}`, "GET");
};

const readBackofficeUserById = async (id) => {
  const rows = await rest.request(getServiceUri() + "backoffice-user/search", "POST", {
    columns: [], query: `id==${Number(id)}`, limit: 2, offset: 0,
  });
  if (!Array.isArray(rows) || rows.length > 1) {
    throw new Error(`Invalid backoffice-user search result for id ${id}`);
  }
  return rows[0] ?? null;
};


const correctVoterApplicationReceiptNumber = async (gid, receiptNumber) => {
  return await rest.request(
    getServiceUri() + `voter-application/${encodeURIComponent(gid)}/correct-receipt-number`,
    "POST",
    { receiptNumber }
  );
};

const pickVotingCenterLabel = (votingCenter) => {
  if (!votingCenter) {
    return "";
  }

  return votingCenter.name || votingCenter.code || "";
};

const buildApplicantName = (application) => {
  const firstName = String(getField(application, "firstName", "first_name")).trim();
  const lastName = String(getField(application, "lastName", "last_name")).trim();

  return [firstName, lastName].filter(Boolean).join(" ");
};

const voterApplicationPortraitSrc = (application) => {
  const id = getField(application, "id", "id");
  const portrait = application?.portrait;
  const hasPortrait = (typeof portrait === "string" || Array.isArray(portrait))
    && portrait.length > 0;

  if (!id || !hasPortrait) {
    return "";
  }

  return `${getServiceUri()}voter-application/portrait/${id}`;
};

const enrichVoterApplication = async (application, gui) => {
  if (!application) {
    return {};
  }

  const votingCenterGid = getField(application, "votingCenterGid", "voting_center_gid");
  const operatorUserGid = getField(application, "operatorUserGid", "operator_user_gid");
  const reviewerBackofficeUserId = getField(
    application, "reviewerBackofficeUserId", "reviewer_backoffice_user_id"
  );

  // These are supplementary records: a missing association must not hide an
  // otherwise readable application. Failures are explicitly reported.
  const related = await Promise.allSettled([
    votingCenterGid ? readByGid("voting-center", votingCenterGid) : Promise.resolve(null),
    operatorUserGid && can(gui, "READ_OPERATOR_USER")
      ? readByGid("operator-user", operatorUserGid)
      : Promise.resolve(null),
    reviewerBackofficeUserId && can(gui, "SEARCH_BACKOFFICE_USER")
      ? readBackofficeUserById(reviewerBackofficeUserId)
      : Promise.resolve(null),
  ]);

  const [votingCenter, operatorUser, reviewerBackofficeUser] = related.map((result, index) => {
    if (result.status === "fulfilled") {
      return result.value;
    }
    console.error(
      `Failed to load ${["voting-center", "operator-user", "backoffice-user"][index]} for voter application ${application.gid}`,
      result.reason
    );
    return null;
  });

  return {
    ...application,

    id: getField(application, "id", "id"),
    gid: getField(application, "gid", "gid"),

    votingCenterGid,
    operatorUserGid,
    reviewerBackofficeUserId,

    votingCenter,
    operatorUser,
    reviewerBackofficeUser,

    applicantName: buildApplicantName(application),
    applicantFirstName: getField(application, "firstName", "first_name"),
    applicantLastName: getField(application, "lastName", "last_name"),
    applicantNationalIdentifier: getField(application, "nationalIdentifier", "national_identifier"),
    applicantBirthDate: getField(application, "birthDate", "birth_date"),

    portraitSrc: can(gui, "READ_VOTER_APPLICATION") ? voterApplicationPortraitSrc(application) : "",

    votingCenterLabel: pickVotingCenterLabel(votingCenter),
    votingCenterName: votingCenter?.name || "",
    votingCenterAddress: votingCenter?.address || "",

    operatorUsername: operatorUser?.username || "",
    reviewerUsername: reviewerBackofficeUser?.username || "",

    receiptNumber: getField(application, "receiptNumber", "receipt_number"),
    submittedAtFormatted: formatDateTime(getField(application, "submittedAt", "submitted_at")),
    reviewedAtFormatted: formatDateTime(getField(application, "reviewedAt", "reviewed_at")),
    statusCodeFormatted: normalizeStatus(getField(application, "statusCode", "status_code")),
    sourceFormatted: formatSource(getField(application, "source", "source")),
    applicationType: getField(application, "applicationType", "application_type"),
    applicationTypeFormatted: applicationTypeLabel(
      getField(application, "applicationType", "application_type")
    ),
  };
};

const ApplicationTypeStamp = ({ applicationType }) => {
  if (normalizeApplicationType(applicationType) !== "CORRECTION") {
    return null;
  }

  return <div className={styles.correctionStamp}>{t("voter.correctionApplication")}</div>;
};

const StatusPill = ({ status }) => {
  const meta = getStatusMeta(status);

  return (
    <div className={`${styles.statusPill} ${meta.className}`}>
      <HowToVoteOutlinedIcon className={styles.statusIcon} />
      <span>{meta.label}</span>
    </div>
  );
};

const SectionCard = ({ icon, title, subtitle, children }) => {
  const Icon = icon;

  return (
    <section className={styles.card}>
      <header className={styles.cardHeader}>
        <div className={styles.cardTitleRow}>
          <div className={styles.cardIconWrap}>
            <Icon className={styles.cardIcon} />
          </div>

          <div className={styles.cardHeaderText}>
            <h2 className={styles.cardTitle}>{title}</h2>
            {subtitle ? <div className={styles.cardSubtitle}>{subtitle}</div> : null}
          </div>
        </div>
      </header>

      <div className={styles.cardBody}>{children}</div>
    </section>
  );
};

const Field = ({ label, value }) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  return (
    <div style={fieldStyle}>
      <div style={fieldLabelStyle}>{label}</div>
      <div style={fieldValueStyle}>{String(value)}</div>
    </div>
  );
};

const LinkedField = ({ label, value, href, gui }) => {
  if (!value) {
    return null;
  }

  return (
    <div style={fieldStyle}>
      <div style={fieldLabelStyle}>{label}</div>
      <button
        type="button"
        style={linkValueStyle}
        onClick={() => gui.goTo(href)}
      >
        {String(value)}
      </button>
    </div>
  );
};

const Portrait = ({ data }) => {
  const [failed, setFailed] = React.useState(false);

  if (!data.portraitSrc || failed) {
    return <div style={portraitBoxStyle}>{t("voter.noPortrait")}</div>;
  }

  return (
    <div style={portraitBoxStyle}>
      <img
        src={data.portraitSrc}
        alt={data.applicantName || t("voter.applicationPhotoAlt")}
        style={portraitImageStyle}
        onError={() => setFailed(true)}
      />
    </div>
  );
};

const ApplicantCardBody = ({ data }) => {
  return (
    <div style={applicantLayoutStyle}>
      <Portrait data={data} />

      <div style={fieldGridStyle}>
        <Field label={t("voter.applicant")} value={data.applicantName} />
        <Field label={t("person.firstName")} value={data.applicantFirstName} />
        <Field label={t("person.lastName")} value={data.applicantLastName} />
        <Field label={t("person.nationalId")} value={data.applicantNationalIdentifier} />
        <Field label={t("voter.birthDate")} value={data.applicantBirthDate} />
      </div>
    </div>
  );
};

const journalValue = (value, fieldKey) => {
  if (value === null || value === undefined || value === "") {
    return "—";
  }

  if (fieldKey === "common.status") {
    return statusLabel(value);
  }

  return String(value);
};

const CorrectableReceiptNumber = ({ data, onCorrect }) => {
  const [editing, setEditing] = React.useState(false);
  const [value, setValue] = React.useState(data.receiptNumber ?? "");
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");

  const cancel = () => {
    setValue(data.receiptNumber ?? "");
    setError("");
    setEditing(false);
  };

  const save = async () => {
    if (value === (data.receiptNumber ?? "") || saving) {
      return;
    }

    setSaving(true);
    setError("");

    try {
      await onCorrect(value);
      setEditing(false);
    } catch (err) {
      setError(err?.message || t("voter.correctionFailed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={fieldStyle} className={styles.correctableField}>
      <div className={styles.correctableFieldHeader}>
        <div style={fieldLabelStyle}>{t("voter.receiptNumber")}</div>

        {!editing ? (
          <button
            type="button"
            className={styles.correctButton}
            onClick={() => setEditing(true)}
          >
            <EditOutlinedIcon className={styles.correctButtonIcon} />
            {t("voter.correctReceiptNumber")}
          </button>
        ) : null}
      </div>

      {!editing ? (
        <div style={fieldValueStyle}>{journalValue(data.receiptNumber)}</div>
      ) : (
        <div className={styles.correctionEditor}>
          <input
            className={styles.correctionInput}
            value={value}
            onChange={(event) => setValue(event.target.value)}
            aria-label={t("voter.newReceiptNumber")}
            autoFocus
          />

          <div className={styles.correctionActions}>
            <button
              type="button"
              className={styles.cancelButton}
              onClick={cancel}
              disabled={saving}
            >
              {t("common.cancel")}
            </button>

            <button
              type="button"
              className={styles.saveButton}
              onClick={save}
              disabled={saving || value === (data.receiptNumber ?? "")}
            >
              {t("voter.saveCorrection")}
            </button>
          </div>

          {error ? <div className={styles.correctionError}>{error}</div> : null}
        </div>
      )}
    </div>
  );
};

const CorrectionJournal = ({ entries }) => {
  const journalEntries = entries.filter((entry) => entry && typeof entry === "object");
  if (!journalEntries.length) {
    return <div className={styles.emptyJournal}>{t("voter.noJournalEntries")}</div>;
  }

  return (
    <div className={styles.journalList}>
      {journalEntries.reverse().map((entry, index) => (
        <div className={styles.journalEntry} key={`${entry.at}-${index}`}>
          <div className={styles.journalEntryHeader}>
            <div className={styles.journalAction}>{t(entry.key)}</div>
            <div className={styles.journalTimestamp}>{formatDateTime(entry.at)}</div>
          </div>

          {entry.reason ? (
            <div className={styles.journalField}>
              {t("voter.journal.reason")}: {t(`voter.journal.reason.${entry.reason}`)}
            </div>
          ) : null}
          {entry.reason === "BIOGRAPHICAL_MISMATCH" && entry.differences?.length ? (
            entry.differences.map((difference) => (
              <div className={styles.journalValues} key={difference.fieldKey}>
                <div>
                  <span>{t("voter.journal.mismatchField")}</span>
                  <strong>{t(difference.fieldKey)}</strong>
                </div>
                <div>
                  <span>{t("voter.journal.applicationValue")}</span>
                  <strong>{journalValue(difference.applicationValue)}</strong>
                </div>
                <div>
                  <span>{t("voter.journal.identifiedPersonValue")}</span>
                  <strong>{journalValue(difference.identifiedPersonValue)}</strong>
                </div>
              </div>
            ))
          ) : null}
          <div className={styles.journalField}>{t(entry.fieldKey)}</div>

          <div className={styles.journalValues}>
            <div>
              <span>{t("voter.oldValue")}</span>
              <strong>{journalValue(entry.oldValue, entry.fieldKey)}</strong>
            </div>
            <div>
              <span>{t("voter.newValue")}</span>
              <strong>{journalValue(entry.newValue, entry.fieldKey)}</strong>
            </div>
            <div>
              <span>{t("voter.journal.performedBy")}</span>
              <strong>
                {entry.actor?.type === "system"
                  ? t("voter.journal.systemActor")
                  : entry.actor?.username || `#${entry.actor?.backofficeUserId ?? entry.actor?.qalaaUserId ?? "—"}`}
              </strong>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

const VoterApplicationHeader = ({ data }) => {
  const status = data.statusCodeFormatted || getField(data, "statusCode", "status_code");
  const correction = isCorrectionApplication(data);

  return (
    <section className={`${styles.header} ${correction ? styles.headerCorrection : ""}`}>
      <div className={styles.headerMain}>
        <div className={styles.heroIconWrap}>
          <AssignmentIndOutlinedIcon className={styles.heroIcon} />
        </div>

        <div className={styles.headerText}>
          <div className={styles.headerTitleRow}>
            <h1 className={styles.headerTitle}>{t("voter.application")}</h1>
            <ApplicationTypeStamp applicationType={data.applicationType} />
          </div>
          <div className={styles.headerSubtitle}>{t("voter.application.subtitle")}</div>
        </div>
      </div>

      <div className={styles.headerAside}>
        <div className={styles.typePill}>{data.applicationTypeFormatted || applicationTypeLabel(data.applicationType)}</div>
        <StatusPill status={status} />
      </div>
    </section>
  );
};

const VoterApplicationReadView = ({ gui, id }) => {
  const [data, setData] = React.useState(null);
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setError("");

        const application = await readByGid("voter-application", id);
        const enriched = await enrichVoterApplication(application, gui);

        if (!cancelled) {
          setData(enriched);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err?.message || String(err));
          setData(null);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [gui, id]);

  if (error) {
    return <div className={styles.error}>{error}</div>;
  }

  if (!data) {
    return <div className={styles.loading}>{t("common.loading")}</div>;
  }

  const correction = isCorrectionApplication(data);

  const saveReceiptNumberCorrection = async (receiptNumber) => {
    const response = await correctVoterApplicationReceiptNumber(id, receiptNumber);

    setData((current) => ({
      ...current,
      receiptNumber: response.receiptNumber,
      journalLog: response.journalLog,
    }));
  };

  return (
    <div className={`${styles.page} ${correction ? styles.pageCorrection : ""}`}>
      <VoterApplicationHeader data={data} />

      <div className={styles.gridTwo}>
        <SectionCard
          icon={PersonOutlineOutlinedIcon}
          title={t("voter.applicant")}
          subtitle={t("voter.identitySnapshotSubtitle")}
        >
          <ApplicantCardBody data={data} />
        </SectionCard>

        <SectionCard
          icon={PlaceOutlinedIcon}
          title={t("voter.votingCenter")}
          subtitle={t("voter.requestedVotingLocation")}
        >
          <div style={fieldGridStyle}>
            <Field
              label={t("voter.votingCenter")}
              value={data.votingCenterLabel}
            />
            <Field label={t("common.address")} value={data.votingCenterAddress} />
          </div>
        </SectionCard>
      </div>

      <SectionCard
        icon={ReceiptLongOutlinedIcon}
        title={t("common.application")}
        subtitle={t("voter.applicationMetadataSubtitle")}
      >
        <div style={fieldGridStyle}>
          <Field label={t("voter.applicationType")} value={data.applicationTypeFormatted} />
          {can(gui, "CORRECT_VOTER_APPLICATION") ? (
            <CorrectableReceiptNumber
              data={data}
              onCorrect={saveReceiptNumberCorrection}
            />
          ) : (
            <Field
              label={t("voter.receiptNumber")}
              value={data.receiptNumber}
            />
          )}
          <Field label={t("common.status")} value={statusLabel(data.statusCodeFormatted)} />
          <Field label={t("common.source")} value={data.sourceFormatted} />
          <Field label={t("voter.submittedAt")} value={data.submittedAtFormatted} />
          <Field label={t("voter.reviewedAt")} value={data.reviewedAtFormatted} />
        </div>
      </SectionCard>

      <SectionCard
        icon={HistoryOutlinedIcon}
        title={t("voter.applicationJournal")}
        subtitle={t("voter.applicationJournalSubtitle")}
      >
        <CorrectionJournal entries={data.journalLog ?? []} />
      </SectionCard>

<SectionCard
  icon={SupervisorAccountOutlinedIcon}
  title={t("reporting.operators")}
  subtitle={t("voter.usersInvolved")}
>
  <div style={fieldGridStyle}>
    <Field
      label={t("voter.operator")}
      value={data.operatorUsername || data.operatorUserGid}
    />
    <Field
      label={t("voter.reviewer")}
      value={data.reviewerUsername || data.reviewerBackofficeUserId}
    />
  </div>
</SectionCard>

<SectionCard
  icon={PlaceOutlinedIcon}
  title={t("voter.gpsCoordinates")}
  subtitle={t("voter.gpsSubtitle")}
>
  <GpsCoordinates
    latitude={data.latitude}
    longitude={data.longitude}
    unavailableText={t("voter.gpsUnavailable")}
  />
</SectionCard>
    </div>
  );
};

export const voterApplicationProjection = {
  sections: [
    {
      title: "voter.applicantSnapshot",
      fields: [
        {
          name: "nationalIdentifier",
          label: "person.nationalId",
          valueType: "text",
          inputType: "text",
          span: 6,
        },
        {
          name: "firstName",
          label: "person.firstName",
          valueType: "text",
          inputType: "text",
          span: 6,
        },
        {
          name: "lastName",
          label: "person.lastName",
          valueType: "text",
          inputType: "text",
          span: 6,
        },
        {
          name: "birthDate",
          label: "voter.birthDate",
          valueType: "date",
          inputType: "date",
          span: 6,
        },
      ],
    },

    {
      title: "common.application",
      fields: [
        {
          name: "votingCenterGid",
          label: "voter.votingCenterGid",
          valueType: "number",
          inputType: "number",
          span: 6,
        },
        {
          name: "receiptNumber",
          label: "voter.receiptNumber",
          valueType: "text",
          inputType: "text",
          span: 6,
        },
        {
          name: "applicationType",
          label: "voter.applicationType",
          valueType: "number",
          inputType: "number",
          span: 6,
        },
        {
          name: "statusCode",
          label: "voter.statusCode",
          valueType: "text",
          inputType: "text",
          span: 6,
        },
        {
          name: "source",
          label: "common.source",
          valueType: "number",
          inputType: "number",
          span: 6,
        },
        {
          name: "submittedAt",
          label: "voter.submittedAt",
          valueType: "datetime",
          inputType: "datetime-local",
          span: 6,
        },
        {
          name: "reviewedAt",
          label: "voter.reviewedAt",
          valueType: "datetime",
          inputType: "datetime-local",
          span: 6,
        },
      ],
    },

    {
      title: "reporting.operators",
      fields: [
        {
          name: "operatorUserGid",
          label: "voter.operatorUserGid",
          valueType: "number",
          inputType: "number",
          span: 6,
        },
        {
          name: "reviewerBackofficeUserId",
          label: "voter.reviewerBackofficeUserId",
          valueType: "number",
          inputType: "number",
          span: 6,
        },
      ],
    },
  ],
};

export const displayReadVoterApplication = (gui, id) => {
  return {
    uuid: `voter-application-read-${id}`,
    view: () => <VoterApplicationReadView gui={gui} id={id} />,
  };
};

export const displayEditVoterApplication = (gui, id, onFinish) => {
  return displayEdit({
    gui,
    projection: voterApplicationProjection,

    loadData: async () => {
      if (!id) {
        return buildEmptyObject(voterApplicationProjection);
      }

      const application = await readByGid("voter-application", id);
      return await enrichVoterApplication(application, gui);
    },

    onSubmit: async (values) => {
      const payload = { ...values };

      delete payload.votingCenter;
      delete payload.operatorUser;
      delete payload.reviewerBackofficeUser;

      delete payload.applicantName;
      delete payload.applicantFirstName;
      delete payload.applicantLastName;
      delete payload.applicantNationalIdentifier;
      delete payload.applicantBirthDate;

      delete payload.portraitSrc;

      delete payload.votingCenterLabel;
      delete payload.votingCenterName;
      delete payload.votingCenterAddress;

      delete payload.operatorUsername;
      delete payload.reviewerUsername;

      delete payload.submittedAtFormatted;
      delete payload.reviewedAtFormatted;
      delete payload.statusCodeFormatted;
      delete payload.sourceFormatted;

      const response = await rest.request(
        getServiceUri() + (id ? "voter-application/update-document" : "voter-application/new-document"),
        "POST",
        payload
      );

      if (onFinish) {
        onFinish(response);
      }

      return response;
    },
  });
};